import { UserResponse } from '@townsquare/identity-client';

import { remoteAuthCheck } from './check';

type AuthCheckResult = {
	isAuthed: boolean;
	isUnauthenticated: boolean;
	isUnauthorised: boolean;
	isNoUser: boolean;
	user: UserResponse | null;
};

const ME_PREFETCH_SCRIPT_ID = 'townsquare-me-bootstrap';

/**
 * Re-shape an inlined `/gateway/api/me` response into the legacy auth-check
 * envelope. Mirrors `remoteAuthCheck`'s mapping so consumers don't need to
 * know whether the data came from SSR injection or a client fetch.
 */
function fromInjectedMe(injected: { status: number; body: unknown }): AuthCheckResult {
	const isUnauthenticated = injected.status === 401;
	return {
		isAuthed: !isUnauthenticated,
		isUnauthenticated,
		isUnauthorised: false,
		isNoUser: false,
		user: (injected.body as UserResponse | null) ?? null,
	};
}

function getErrorMessage(error: unknown): string {
	return error instanceof Error ? `${error.name}: ${error.message}` : String(error);
}

function getInjectedMe(): { status: number; body: unknown } | undefined {
	const script = document.getElementById(ME_PREFETCH_SCRIPT_ID);
	if (!script?.textContent) {
		return undefined;
	}
	try {
		return JSON.parse(script.textContent) as { status: number; body: unknown };
	} catch (error) {
		console.warn(
			`[me-prefetch] failed to parse ${ME_PREFETCH_SCRIPT_ID} JSON (${getErrorMessage(error)})`,
		);
		return undefined;
	}
}

/**
 * Separate file to make mocking easier in tests.
 *
 * When the Volt controller has inlined `globalThis.__TOWNSQUARE_ME__`, skip
 * the in-browser fetch and resolve straight from the inlined payload. Falls
 * back to `remoteAuthCheck()` when the global is absent (legacy serving path,
 * SSR fetch failure).
 */
export const prefetchedAuthCheckPromise: Promise<AuthCheckResult> = (() => {
	const injected = getInjectedMe();
	if (injected) {
		return Promise.resolve(fromInjectedMe(injected));
	}
	return remoteAuthCheck();
})();
